import { Button, Stack } from "@mui/material";
import { Typography } from "@mui/material";
import { useInviteLeadMutation } from "Api/Mutations/Lead/useLeadInvitationMutation";
import { LeadInvitationForm } from "Components/Affiliate/LeadInvitationForm";
import { LeadInvitationCommandResultStatus } from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import type { AffiliateUserRole } from "Types/types";

const LEAD_RESOURCES = Resources.Components.Affiliate.LeadInvitation;

const getErrorMessageForLeadStatus = (
  status: LeadInvitationCommandResultStatus,
  t: TFunction,
) => {
  // biome-ignore format: error per line
  const map: Record<LeadInvitationCommandResultStatus, string> = {
      [LeadInvitationCommandResultStatus.Success]: "",
      [LeadInvitationCommandResultStatus.Error]: t(LEAD_RESOURCES.GeneralError),
      [LeadInvitationCommandResultStatus.UserExists]: t(LEAD_RESOURCES.UserAlreadyExists),
      [LeadInvitationCommandResultStatus.LeadExists]: t(LEAD_RESOURCES.InvitationAlreadySent),
    };

  return map[status];
};

type Props = { invitedUserRole: AffiliateUserRole };

export const EmailInvitation = ({ invitedUserRole }: Props) => {
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [leadInvitationStatus, setLeadInvitationStatus] =
    useState<LeadInvitationCommandResultStatus | null>(null);
  const { t } = useResource();

  const { mutate: inviteLead, isPending: isLoading } = useInviteLeadMutation({
    onSettled: (status: LeadInvitationCommandResultStatus) => {
      setLeadInvitationStatus(status);
      setIsSuccess(status === LeadInvitationCommandResultStatus.Success);
    },
  });

  const errors =
    leadInvitationStatus &&
    leadInvitationStatus !== LeadInvitationCommandResultStatus.Success
      ? {
          email: {
            type: "manual",
            message: getErrorMessageForLeadStatus(leadInvitationStatus, t),
          },
        }
      : undefined;

  if (isSuccess) {
    return (
      <Stack
        gap={2}
        minHeight="150px"
        alignItems="center"
        justifyContent="center"
      >
        <Typography>{t(LEAD_RESOURCES.FriendInvited)}</Typography>
        <Button onClick={() => setIsSuccess(null)} variant="text" color="info">
          {t(LEAD_RESOURCES.NewInvitation)}
        </Button>
      </Stack>
    );
  }

  return (
    <LeadInvitationForm
      isLoading={isLoading}
      errors={errors}
      invitedUserRole={invitedUserRole}
      onSubmit={({ email }) => {
        inviteLead({ email, userRole: invitedUserRole });
      }}
    />
  );
};
